// The wake detector's arithmetic: an adaptive noise floor, an onset rule and a
// bounded pre-roll ring, over Float32Array and nothing else.
//
// It lives apart from src/wake-worklet.ts because an AudioWorklet cannot be
// instantiated in a test — happy-dom has neither AudioContext nor
// AudioWorkletProcessor — so the only way the ring and the drain rule get a
// test at all is for them to be a module with no Web Audio in it
// (docs/wake-on-speech.md §10). Nothing here touches a global: it is arithmetic
// and it runs anywhere.
//
// The ring is the client analogue of `preroll` in internal/session/ingress.go:
// a fixed number of frames, buffers reused, oldest dropped rather than grown.
// src/index.ts imports WAKE_DEFAULTS and WakeTuning from here and nothing else.

/** Samples in one render quantum. Fixed at 128 by the Web Audio spec: 2.67 ms at 48 kHz. */
export const RENDER_QUANTUM = 128;

/**
 * dBFS reported for digital silence, and the floor of every level in here.
 *
 * `20·log10(0)` is `-Infinity`, which poisons an exponential average forever;
 * a floor keeps the arithmetic finite and -100 dBFS is far below anything a
 * capture device produces that is not exactly zero.
 */
export const DBFS_FLOOR = -100;

/**
 * The loudest level this detector will still call silence, whatever the floor.
 *
 * -54 dBFS is the noise floor the live-smoke script had to add so Chrome would
 * keep emitting RTP at all, and turns committed 5/5 at it (TODO.md 29): it is a
 * measured "this is silence", not a level to trigger on. It guards the case an
 * adaptive floor alone gets wrong — a capture that starts at exact zeros floors
 * at -100 dBFS, and then any room tone at all clears `floor + marginDb`.
 */
export const SILENCE_DBFS = -54;

/**
 * How far over the floor a quantum may stand and still be drained, in dB.
 *
 * The floor is the lower envelope of a room, not its mean — an asymmetric
 * average that falls in 200 ms and rises in 5 s settles below almost every
 * quantum of stationary room tone, so "at or below the floor" drops about a
 * tenth of a silent room and the backlog never pays down. Six dB is half the
 * onset rule's `marginDb`, which is the property that matters: a quantum the
 * onset rule would have called speech can never be dropped, and everything
 * quieter than halfway to speech is the pause it looks like.
 */
export const DRAIN_MARGIN_DB = 6;

/**
 * How much capture to hold while armed, in milliseconds.
 *
 * Sized from M1, measured 2026-09-14 (`NOTES.md` Measurements): onset detection
 * costs `sustainMs` = 150 ms, the wake handshake to the first RTP frame the
 * server accepts is 1003 ms at p90 over an emulated 40 ms RTT with the released
 * socket gone, and a 750 ms ring covers the whole first word on 94.2 % of clips
 * (docs/wake-on-speech.md §6). 150 + 1003 + 750 = 1903, so 1900 ms — and that
 * is the floor, not a cushion: the M1 driver spoke `ws://` to a loopback daemon,
 * so a real TLS open adds one or two further round trips on top. At 48 kHz it
 * costs 365 KB. It is one constant in one place so a better M1 changes it here
 * and nowhere else.
 */
export const PRE_ROLL_MS_DEFAULT = 1900;

/** Time constant for a floor following the room down: fast, so a room that quietens is tracked at once. */
const FLOOR_FALL_MS = 200;

/**
 * Time constant for a floor following the room up: slow, so speech cannot lift
 * the floor out from under its own onset, but a room that really does get
 * louder is tracked within tens of seconds rather than never.
 */
const FLOOR_RISE_MS = 5_000;

/**
 * How fast a sustain run decays over a quantum under the threshold, as a
 * multiple of real time. Four means a run tolerates a gap of up to a quarter of
 * the speech it has already accumulated — enough for a stop closure, nowhere
 * near enough for a door that bangs once every second.
 */
const SUSTAIN_DECAY = 4;

/**
 * The wake detector's tuning. Every field is required here; the public option
 * in src/index.ts is partial and merges over WAKE_DEFAULTS.
 *
 * `sustainMs` sits deliberately below Silero's 200 ms debounce
 * (internal/vad/silero.go): this detector's job is not to be right about
 * speech — the server's VAD stays authoritative — but to start the reconnect
 * early enough that the ring still holds the word.
 */
export interface WakeTuning {
	/** Capture held while armed, paid out ahead of live audio on a wake. */
	preRollMs: number;
	/** How long the level must stay over the floor before an onset is declared. */
	sustainMs: number;
	/** How far over the tracked noise floor counts as speech, in dB. */
	marginDb: number;
	/** Ceiling on wakes per minute, enforced by the session, not by this module. */
	maxWakesPerMinute: number;
}

/** Starting points, not tuned values: §11's M2 replaces `sustainMs` and `marginDb`. */
export const WAKE_DEFAULTS: WakeTuning = {
	preRollMs: PRE_ROLL_MS_DEFAULT,
	sustainMs: 150,
	marginDb: 12,
	maxWakesPerMinute: 4,
};

/** Root mean square of one quantum. Zero for an empty block. */
export function rms(block: Float32Array): number {
	if (block.length === 0) return 0;
	let sum = 0;
	for (let i = 0; i < block.length; i++) {
		const x = block[i] ?? 0;
		sum += x * x;
	}
	return Math.sqrt(sum / block.length);
}

/**
 * Amplitude to dBFS, clamped into [DBFS_FLOOR, 0] so that every level this
 * module works in is a finite number.
 *
 * Both ends are load-bearing. `20·log10(0)` is `-Infinity`, and an
 * `-Infinity` in an exponential average poisons it forever. The top is the
 * same failure one platform bug away: a single non-finite sample — a device
 * glitch, a driver handing back garbage — makes `rms` infinite, the floor
 * infinite, and the next quantum's `level - floor` a NaN, against which every
 * comparison is false. The detector would then be deaf for the life of the
 * AudioContext, with nothing observable to say so. Above 0 dBFS is not
 * physical anyway, so clamping there costs nothing and makes the estimator
 * total over anything a platform can hand it. A NaN amplitude fails the
 * `> 0` test and floors.
 */
export function dbfs(amplitude: number): number {
	if (!(amplitude > 0)) return DBFS_FLOOR;
	return Math.min(0, Math.max(DBFS_FLOOR, 20 * Math.log10(amplitude)));
}

/**
 * The input meter's accumulator: one report's worth of RMS and peak-hold, in
 * one pass over each quantum and with nothing allocated per quantum.
 *
 * It exists as a separate object from the detector's floor because the two
 * measure different things over different windows. The detector wants one
 * level per quantum to compare against a floor it is tracking; a meter wants
 * the whole interval — RMS over every sample the user spoke into, and the
 * loudest single sample in it, because a bar drawn from RMS alone never shows
 * the clip a user has to be told about.
 *
 * `add` answers the quantum's own RMS so a caller that needs both — the
 * worklet does, every quantum, for the onset rule — pays for one pass over
 * the samples and not two. The sum of squares is accumulated rather than the
 * per-quantum RMS averaged: averaging RMS values would be a mean of roots and
 * not the root of the mean, which under-reports a burst inside an otherwise
 * quiet interval, and a meter's whole job is to show that burst.
 */
export class LevelMeter {
	private sumSq = 0;
	private samples = 0;
	private peak = 0;

	/** Folds one quantum in and returns its own RMS amplitude. */
	add(block: Float32Array): number {
		let sum = 0;
		let peak = 0;
		for (let i = 0; i < block.length; i++) {
			const x = block[i] ?? 0;
			sum += x * x;
			// `Math.abs` rather than a squared comparison: the square is already
			// in `sum` but the peak has to survive the interval as an amplitude,
			// and one abs per sample is cheaper than a root per quantum.
			const mag = Math.abs(x);
			if (mag > peak) peak = mag;
		}
		this.sumSq += sum;
		this.samples += block.length;
		if (peak > this.peak) this.peak = peak;
		return block.length === 0 ? 0 : Math.sqrt(sum / block.length);
	}

	/** RMS over everything added since the last reset, in dBFS. */
	get db(): number {
		return this.samples === 0 ? DBFS_FLOOR : dbfs(Math.sqrt(this.sumSq / this.samples));
	}

	/** The loudest single sample since the last reset, in dBFS. */
	get peakDb(): number {
		return dbfs(this.peak);
	}

	/**
	 * Starts the next interval. Peak-hold is per report and not cumulative: a
	 * peak that never decayed would leave every meter on the page pinned at
	 * the loudest thing that ever happened to it.
	 */
	reset(): void {
		this.sumSq = 0;
		this.samples = 0;
		this.peak = 0;
	}
}

/**
 * A slowly-adapting estimate of the room, in dBFS.
 *
 * Asymmetric on purpose: it follows the room down in 200 ms and up in 5 s, which
 * is the cheapest estimator that both settles quickly on a quiet room and cannot
 * be dragged up by a few hundred milliseconds of speech. The first observation
 * primes it outright — starting at -100 dBFS and climbing would spend the first
 * minute of every session reporting every sound as 40 dB over the floor.
 */
class NoiseFloor {
	private readonly fall: number;
	private readonly rise: number;
	private value = DBFS_FLOOR;
	private primed = false;

	constructor(sampleRate: number, quantum: number) {
		const stepMs = (quantum / Math.max(1, sampleRate)) * 1000;
		// Per-quantum weight of an exponential average with that time constant.
		this.fall = 1 - Math.exp(-stepMs / FLOOR_FALL_MS);
		this.rise = 1 - Math.exp(-stepMs / FLOOR_RISE_MS);
	}

	get db(): number {
		return this.value;
	}

	/**
	 * Folds one quantum's level in and returns the floor.
	 *
	 * `hold` freezes the estimate while a candidate onset is accumulating, so
	 * that the threshold a run is being measured against cannot move under it.
	 * An unprimed floor is primed anyway: a hold against a floor that has never
	 * seen a sample would latch forever.
	 */
	observe(levelDb: number, hold = false): number {
		if (!this.primed) {
			this.primed = true;
			this.value = levelDb;
			return this.value;
		}
		if (hold) return this.value;
		this.value += (levelDb - this.value) * (levelDb < this.value ? this.fall : this.rise);
		return this.value;
	}
}

/**
 * The onset rule: level over `floor + marginDb`, and over SILENCE_DBFS in
 * absolute terms, sustained for `sustainMs`.
 *
 * It owns its floor because the two are one rule — the floor is held still
 * while a run accumulates (see NoiseFloor.observe) and tracked freely
 * otherwise, and splitting them would leave that coupling in the caller.
 * `tuning` is held by reference, so a `tune` message that mutates it in place
 * takes effect on the next quantum with no plumbing.
 */
export class OnsetDetector {
	private readonly floor: NoiseFloor;
	private readonly quantumMs: number;
	private readonly tuning: WakeTuning;
	private heldMs = 0;
	private latched = false;

	constructor(sampleRate: number, tuning: WakeTuning, quantum: number = RENDER_QUANTUM) {
		this.floor = new NoiseFloor(sampleRate, quantum);
		this.quantumMs = (quantum / Math.max(1, sampleRate)) * 1000;
		this.tuning = tuning;
	}

	get floorDb(): number {
		return this.floor.db;
	}

	/**
	 * Keeps the floor warm while nothing is listening for an onset, so that the
	 * estimate is already settled the moment the session arms. Any run in
	 * progress is abandoned.
	 */
	track(levelDb: number): number {
		this.reset();
		return this.floor.observe(levelDb);
	}

	/**
	 * Folds one quantum's level in and returns true on the single quantum where
	 * an onset is declared. Latched until the run decays away, so one utterance
	 * is one onset.
	 *
	 * A quantum under the threshold decays the run instead of zeroing it. A
	 * render quantum is 2.67 ms and speech is full of gaps that long and longer
	 * — a stop closure, a glottal break — so a rule that wanted `sustainMs`
	 * *contiguous* would miss plosive-initial speech entirely, and neither knob
	 * this module exposes could fix that. Decaying at four times real time keeps
	 * what the sustain was for (one loud door is not a word: a 30 ms bang is
	 * gone 7.5 ms into the silence behind it) while a run survives a break of up
	 * to a quarter of what it has accumulated.
	 */
	observe(levelDb: number): boolean {
		if (!(levelDb > SILENCE_DBFS && levelDb - this.floor.db >= this.tuning.marginDb)) {
			this.heldMs = Math.max(0, this.heldMs - this.quantumMs * SUSTAIN_DECAY);
			if (this.heldMs === 0) this.latched = false;
			this.floor.observe(levelDb);
			return false;
		}
		this.heldMs += this.quantumMs;
		const fire = !this.latched && this.heldMs >= this.tuning.sustainMs;
		if (fire) this.latched = true;
		// The floor is frozen while the run is still a candidate: a threshold
		// that drifts up under the very run being measured against it is a
		// detector that never fires. Once latched it tracks again, so a room
		// that really did get louder becomes the floor within seconds and the
		// next word is heard against it instead of latching forever.
		this.floor.observe(levelDb, !this.latched);
		return fire;
	}

	/** Abandons any run in progress. The floor is an estimate of the room and survives. */
	reset(): void {
		this.heldMs = 0;
		this.latched = false;
	}
}

/** Copies one quantum in, padding or truncating: `renderQuantumSize` is 128 today but is not promised. */
function copyQuantum(into: Float32Array, from: Float32Array): void {
	if (from.length === into.length) {
		into.set(from);
		return;
	}
	if (from.length > into.length) {
		for (let i = 0; i < into.length; i++) into[i] = from[i] ?? 0;
		return;
	}
	into.set(from);
	into.fill(0, from.length);
}

/**
 * A bounded ring of the most recent `preRollMs` of capture, in whole quanta.
 *
 * Every buffer is allocated once and reused, and a push into a full ring drops
 * the oldest quantum rather than growing: a wake that takes longer than the ring
 * must clip the opening audibly, not hide it behind an unbounded buffer
 * (docs/wake-on-speech.md §6).
 *
 * In payout the same ring carries the backlog: shift one quantum out, push the
 * live one in behind it, and the occupancy — the offset the user is speaking
 * from — stays exactly where the wake left it until a drain pays it down.
 */
export class WakeRing {
	/** Samples per quantum. */
	readonly quantum: number;
	/** Quanta the ring can hold. */
	readonly capacity: number;
	/** Milliseconds one quantum represents at this graph's sample rate. */
	readonly quantumMs: number;

	private readonly frames: Float32Array[];
	private readonly excess: Float32Array;
	private head = 0;
	private count = 0;

	constructor(sampleRate: number, preRollMs: number, quantum: number = RENDER_QUANTUM) {
		const rate = Math.max(1, sampleRate);
		this.quantum = Math.max(1, Math.trunc(quantum));
		this.quantumMs = (this.quantum / rate) * 1000;
		this.capacity = Math.max(1, Math.round((Math.max(0, preRollMs) * rate) / 1000 / this.quantum));
		this.frames = Array.from({ length: this.capacity }, () => new Float32Array(this.quantum));
		this.excess = new Float32Array(this.capacity);
	}

	/** Quanta currently held. */
	get size(): number {
		return this.count;
	}

	/** Milliseconds currently held: the offset a payout is running at. */
	get heldMs(): number {
		return this.count * this.quantumMs;
	}

	/** How far the oldest held quantum stood over the floor when it was captured, in dB. */
	get headExcessDb(): number {
		return this.count === 0 ? 0 : (this.excess[this.head] ?? 0);
	}

	/**
	 * Pushes one quantum and how far it stood over the noise floor **at the
	 * moment it was captured**, in dB.
	 *
	 * That, rather than an absolute level, is what the drain rule needs: by the
	 * time a backlog quantum is considered for dropping, the floor has moved —
	 * the speech in the backlog dragged it up, and the room may have quietened
	 * since — and comparing then-audio against now-floor drops words. The
	 * question is always "was this silence when it happened", and this is that
	 * question's answer, computed when it can still be answered.
	 *
	 * Returns whether the oldest quantum was dropped to make room.
	 */
	push(block: Float32Array, excessDb: number): boolean {
		const full = this.count === this.capacity;
		if (full) {
			this.head = (this.head + 1) % this.capacity;
			this.count--;
		}
		const at = (this.head + this.count) % this.capacity;
		const frame = this.frames[at];
		if (frame) copyQuantum(frame, block);
		this.excess[at] = excessDb;
		this.count++;
		return full;
	}

	/** Copies the oldest quantum into `out` and drops it. False, and `out` untouched, when empty. */
	shift(out: Float32Array): boolean {
		if (this.count === 0) return false;
		const frame = this.frames[this.head];
		if (frame) copyQuantum(out, frame);
		this.head = (this.head + 1) % this.capacity;
		this.count--;
		return true;
	}

	/**
	 * The drain rule. Drops the oldest backlog quantum if and only if it stood
	 * less than DRAIN_MARGIN_DB over the noise floor when it was captured — a
	 * pause the server is not measuring, removed — and never a voiced one, since
	 * that margin is half the one the onset rule calls speech. A user who never
	 * pauses keeps the offset, which is the honest answer
	 * (docs/wake-on-speech.md §6).
	 */
	dropSilent(): boolean {
		if (this.count === 0 || !(this.headExcessDb < DRAIN_MARGIN_DB)) return false;
		this.head = (this.head + 1) % this.capacity;
		this.count--;
		return true;
	}

	/** Forgets everything held. The buffers stay. */
	clear(): void {
		this.head = 0;
		this.count = 0;
	}
}
